import { FC, useEffect, useMemo, useRef, useState } from "react";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import useDataList from "@/hooks/useDataList";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { Button, Flex, Input, Select, TableColumnsType, Tag, App } from "antd";
import { useTranslation } from "react-i18next";
import { CommonListParams } from "@/types/common";
import SText from "@/styles/SText";
import dayjs from "dayjs";
import apis, { SupplierApproveStateEnum, supplierPriceRequestAdd, supplierPriceRequestUpdate, SupplierPriceStateEnum } from "@/apis/supplier";
import { getStorage } from "@/utils/storage";
import { useLocation } from "react-router";
import { dateFormat, priceFormat } from "@/utils/format";
import { ProductSupplierPrice, SupplierApprovePrice, SupplierPriceStateEnum as SupplierPriceStateEnumT, SupplierPriceRequestAdd, SupplierPriceAdd, SupplierApprovePriceStateEnum } from "@/types/purchase";
import usePriceStatus from "./hooks/usePriceStatus";
import SupplierPriceForm from "@/components/SupplierPriceForm";
import { CurrencySelect } from "@/components/Selector";

const SupplierQuotationList: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { notification } = App.useApp();

  const tableRef = useRef<TableLayoutRef | null>(null);

  const [keyword, setKeyword] = useState<string>("");
  const [priceStatus, setPriceStatus] = useState<SupplierPriceStateEnum | null>(null);
  const [approveState, setApproveState] = useState<SupplierApproveStateEnum | null>(null);
  const [dataList, setDataList] = useState<Array<ProductSupplierPrice | SupplierApprovePrice>>([]);
  const [detail, setDetail] = useState<ProductSupplierPrice | SupplierApprovePrice | null>(null);
  const [formType, setFormType] = useState<'add' | 'update'>('add');
  const [supplierInfo, setSupplierInfo] = useState<any>({})
  const [addLoading, setAddLoading] = useState<boolean>(false);
  
  const { getProductPriceStatus, getSupplierPriceStatus } = usePriceStatus()
  
  const pageType: 'my' | 'product' = useMemo(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      const pageType = params.get("pageType") || "";
      if (pageType === 'my') return 'my'
    }
    return 'product'
  }, [location.search]);
  const [, total, listLoading, { fetchList: fetchQuotationRequestItems }] =
    useDataList<any, [string, any, CommonListParams]>({
      fetchApi:
        pageType === 'my'
          ? apis.supplierQuotationsApprove
          : apis.supplierQuotationsProduct,
    });

  useEffect(() => {
    if (pageType) {
      fetchList(0, keyword);
      tableRef.current && tableRef.current.resetTablePage()
    }
  }, [pageType, approveState, priceStatus])

  useEffect(()=>{
    apis.fetch(getStorage("SUPPLIER_ID")).then(res =>{
      setSupplierInfo(res)
    })
  },[])

  const fetchList = (offset: number, keyword: string) => {

    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    fetchQuotationRequestItems(getStorage("SUPPLIER_ID"), pageType === 'my' ? (approveState ?? SupplierApproveStateEnum.ALL) : (priceStatus ?? SupplierPriceStateEnum.ALL), {
      body: keyword,
      pagination: { offset, take },
    }).then(({ body }) => {
      setDataList(body ?? []);
    });
  };

  const columns: TableColumnsType<ProductSupplierPrice | SupplierApprovePrice> = useMemo(() => [
    {
      title: t("sale.productInfo"),
      children: [
        {
          title: t("supplierQuotation.productCode"),
          width: 120,
          render: (_, item) => (
            <SText text={item?.product?.code || ''} />
          ),
        },
        {
          title: t("supplierQuotation.productName"),
          width: 140,
          render: (_, item) => (
            <SText text={item?.product?.name || ''} />
          ),
        },
        {
          title: t("supplierQuotation.productModel"),
          width: 200,
          render: (_, item) => (
            <SText text={item?.product?.model || ''} />
          ),
        },
        {
          title: <SText text={t("product.uom")} />,
          width: 120,
          key: "uom",
          render: (_, item) => <SText text={item.product?.uom?.toLocaleUpperCase() ?? ""} />,
        },
      ]
    },
    {
      title: t("requirement.requirementInfo"),
      children: [

        {
          title: <SText text={t("purchase.stPrice")} />,
          width: 100,
          key: "price",
          render: (_, item) => <SText text={
            priceFormat(pageType === 'my' ? ((item as SupplierApprovePrice).price ?? "") :
              ((item as ProductSupplierPrice).priceRequest?.price ?? "")
            )} />,
        },
        {
          title: <SText text={t("purchase.currency")} />,
          dataIndex: "currency",
          width: 100,
          render: (val, item) => (
            <CurrencySelect key={item.id} value={pageType === 'my' ? (item as SupplierApprovePrice).currency : (item as ProductSupplierPrice)?.priceRequest?.currency} readOnly={true} />
          ),
        },
        {
          title: t("purchase.expiredAt"),
          width: 120,
          key: "expiredAt",
          render: (_, item) => {
            if (pageType === 'my') return (item as SupplierApprovePrice).expiredAt && (item as SupplierApprovePrice).expiredAt !== 0 && dateFormat(dayjs((item as SupplierApprovePrice).expiredAt))
            else {
              let record: ProductSupplierPrice = { ...item } as ProductSupplierPrice
              return record.priceRequest && record.priceRequest.expiredAt && record.priceRequest.expiredAt !== 0 && dateFormat(dayjs(record.priceRequest.expiredAt))
            }
          },
        },
        {
          title: t("purchase.days"),
          width: 80,
          key: "days",
          render: (_, item) => {
            if (pageType === 'my') return (item as SupplierApprovePrice).days
            else {
              let record: ProductSupplierPrice = { ...item } as ProductSupplierPrice
              return record.priceRequest && record.priceRequest.days
            }
          },
        },
        {
          title: t("purchase.mpq"),
          width: 80,
          key: "mpq",
          render: (_, item) => {
            if (pageType === 'my') return (item as SupplierApprovePrice).mpq
            else {
              let record: ProductSupplierPrice = { ...item } as ProductSupplierPrice
              return record.priceRequest && record.priceRequest.mpq
            }
          },
        },
      ]
    },
    {
      title: t("supplierQuotation.status"),
      key: "status",
      fixed: 'right',
      width: 140,
      render: (_, item) => {
        if (pageType === 'my') {
          let record: SupplierApprovePrice = { ...item } as SupplierApprovePrice;
          let info = getSupplierPriceStatus(record.approveState)
          if (info) return record.expiredAt < Date.now() ? <><Tag color={info.color}>{info.title}</Tag><span style={{ color: '#ff5500' }}>{`(${t('requirement.timed')})`}</span></> : <Tag color={info.color}>{info.title}</Tag>;
        } else {
          let record: ProductSupplierPrice = { ...item } as ProductSupplierPrice;
          if (record.priceRequest) {
            let info = getProductPriceStatus(record.priceRequest.state)
            if (info) return record.priceRequest.expiredAt < Date.now() ? <><Tag color={info.color}>{info.title}</Tag><span style={{ color: '#ff5500' }}>{`(${t('requirement.timed')})`}</span></> : <Tag color={info.color}>{info.title}</Tag>;
          } else return <Tag color='#2db7f5'>{t("requirement.unPriced")}</Tag>;
        }
      },
    },
    {
      title: t("supplierQuotation.operation"),
      width: 140,
      fixed: "right",
      dataIndex: "opt",
      render: (_, item) => {
        if (pageType === 'product') {
          let record: ProductSupplierPrice = { ...item } as ProductSupplierPrice
          if (record.priceRequest) {
            if (approveTimedOut(record.priceRequest)) return <>
              <Button type="link" onClick={() => { setDetail(item); setFormType('update') }}>{t("common.detail")}</Button>
              {/* <Button type="primary" onClick={() => { setDetail(item); setFormType('add') }}>{t("requirement.rePrice")}</Button> */}
            </>
            return <Button type="link" onClick={() => { setDetail(item); setFormType('update') }}>{t("common.detail")}</Button>
          }
          else return <Button type="primary" onClick={() => { setDetail(item); setFormType('add') }}>{t("supplierLayout.quotations")}</Button>
        } else {
          let record: SupplierApprovePrice = { ...item } as SupplierApprovePrice
          if (approveTimedOut(record) || record.approveState === SupplierApprovePriceStateEnum.REJECTED) return (
            <>
              <Button type="link" onClick={() => { setDetail(item); setFormType('update') }}>{t("common.detail")}</Button>
              {/* <Button type="primary" onClick={() => { setDetail(item); setFormType('add') }}>{t("requirement.rePrice")}</Button> */}
            </>
          )
          else return <Button type="link" onClick={() => { setDetail(item); setFormType('update') }}>{t("common.detail")}</Button>
        }
      },
    },
  ], [pageType])

  const approveTimedOut = (price: SupplierApprovePrice) => {
    return price.approveState === null && price.expiredAt < Date.now()
  }

  const needUpdate = (data: SupplierPriceAdd) => {
    if (pageType === 'product') {
      let originData: ProductSupplierPrice = { ...detail } as ProductSupplierPrice
      return !!originData.priceRequest && (
        data.price !== originData.priceRequest.price
        || data.expiredAt !== originData.priceRequest.expiredAt
        || data.mpq !== originData.priceRequest.mpq
        || data.days !== originData.priceRequest.days
        || data.remark !== originData.priceRequest.remark)
    } else {
      let originData: SupplierApprovePrice = { ...detail } as SupplierApprovePrice
      return data.price !== originData.price
        || data.expiredAt !== originData.expiredAt
        || data.mpq !== originData.mpq
        || data.days !== originData.days
        || data.remark !== originData.remark
    }
  }

  const onOk: (data: SupplierPriceAdd) => Promise<void> = (data: SupplierPriceAdd) => {
    setAddLoading(true)
    let supplierPriceRequest: SupplierPriceRequestAdd = { ...data, supplierId: getStorage('SUPPLIER_ID') }

    if (formType === 'add') {
      return new Promise((resolve, reject) => {
        supplierPriceRequestAdd({ ...supplierPriceRequest })
          .then((res) => {
            if (pageType === 'product') setDataList(last => {
              return last.map(item => {
                let record: ProductSupplierPrice = { ...item } as ProductSupplierPrice
                if (res.productId === record.product.id) record.priceRequest = { ...res, approveState: null, state: SupplierPriceStateEnumT.PRICED }
                return record
              })
            })
            else setDataList(last => [{ ...res, product: (detail as SupplierApprovePrice).product }, ...last])
            setDetail(null)
            resolve();
          })
          .catch((err) => {
            notification.error({ placement: 'top',
              message: t("common.addError"),
              description: err === 71701 ? t("purchase.priced") : t("common.serverError"),
            });
          })
          .finally(() => setAddLoading(false))
      });
    } else {
      if (needUpdate(data)) {
        return new Promise((resolve, reject) => {
          supplierPriceRequest.approveState = null
          supplierPriceRequestUpdate((detail as ProductSupplierPrice).priceRequest?.id || '', { ...supplierPriceRequest })
            .then(res => {
              if (pageType === 'product') setDataList(last => {
                return last.map(item => {
                  let record: ProductSupplierPrice = { ...item } as ProductSupplierPrice;
                  if (record.priceRequest && res.id === record.priceRequest.id) return { ...record, priceRequest: { ...res } }
                  else return record
                })
              })
              else setDataList(last => {
                return last.map(item => {
                  let record: SupplierApprovePrice = { ...item } as SupplierApprovePrice;
                  if (res.id === record.id) return { ...res }
                  else return record
                })
              })
              setDetail(null)
              resolve();
            })
        })
      } else {
        setDetail(null)
        setAddLoading(false)
        return new Promise(() => { })
      }
    }
  }

  const headerChildren = <Flex justify="flex-end" style={{ width: '100%' }} gap={20}>
    {pageType && pageType === 'product' ? <Select
      style={{ width: 120 }}
      placeholder={t('requirement.status')}
      size="small"
      options={[
        { label: t('requirement.unPriced'), value: SupplierPriceStateEnum.UN_PRICED, key: 0 },
        { label: t('requirement.resolved'), value: SupplierPriceStateEnum.PRICED, key: 1 },
      ]}
      value={priceStatus}
      onChange={val => setPriceStatus(val)}
      allowClear
    /> : <Select
      style={{ width: 120 }}
      placeholder={t('requirement.status')}
      size="small"
      options={[
        { label: t('requirement.unApprove'), value: SupplierApproveStateEnum.PENDING, key: 0 },
        { label: t('requirement.approved'), value: SupplierApproveStateEnum.RESOLVED, key: 1 },
        { label: t('requirement.rejected'), value: SupplierApproveStateEnum.REJECTED, key: 2 },
      ]}
      value={approveState}
      onChange={val => setApproveState(val)}
      allowClear
    />}
    <Input.Search
      enterButton
      placeholder={t("requirement.searchPro")}
      value={keyword}
      onChange={(e) => setKeyword(e.target.value)}
      onSearch={(e) => {
        fetchList(0, e);
        tableRef?.current?.resetTablePage();
      }}
    />
  </Flex>

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={dataList}
        rowKey="id"
        scrollX={1200}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={(offset) => {
          fetchList(offset, keyword);
        }}
      />
      {pageType === 'product' && detail && <SupplierPriceForm
        propProductList={[(detail as ProductSupplierPrice).product]}
        key={"addPrice"}
        open={true}
        confirmLoading={addLoading}
        handleCancel={() => setDetail(null)}
        handleOk={onOk}
        leftTitle={formType === 'update' ? t('common.detail') : t('supplierLayout.quotations')}
        type={formType}
        id={(detail as ProductSupplierPrice).priceRequest ? (detail as ProductSupplierPrice).priceRequest?.id || '' : ''}
        changeType={() => setFormType('add')}
        supplierInfo={supplierInfo}
      />}
      {pageType === 'my' && detail && <SupplierPriceForm
        propProductList={[(detail as ProductSupplierPrice).product]}
        open={true}
        confirmLoading={addLoading}
        handleCancel={() => setDetail(null)}
        handleOk={onOk}
        leftTitle={formType === 'add' ? t('requirement.rePrice') : t("common.detail")}
        type={formType}
        id={(detail as ProductSupplierPrice).id || ''}
        changeType={() => setFormType('add')}
        supplierInfo={supplierInfo}
      />}
    </div>
  );
};

export default SupplierQuotationList;
